import { useState } from 'react';
import { IconSearch, IconLoader2 } from '@tabler/icons-react';
import api from '../lib/api';
import PageHeader from '../components/PageHeader';
import MainCard from '../components/MainCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

export function WalletPage() {
  const [msisdn, setMsisdn] = useState('');
  const [wallets, setWallets] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!msisdn.startsWith('27') || msisdn.length !== 11) {
        throw new Error('MSISDN must be 11 digits starting with 27');
      }

      const res = await api.get(`/wallet/details?msisdn=${msisdn}`);
      setWallets(res.data.data || []);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Search failed');
      setWallets([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Wallet Query"
        subtitle="Search customer wallet details by MSISDN"
      />

      <MainCard className="mb-6">
        <form onSubmit={handleSearch} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="msisdn">MSISDN (South African Number)</Label>
            <Input
              id="msisdn"
              value={msisdn}
              onChange={(e) => setMsisdn(e.target.value)}
              placeholder="27821234567"
              required
              pattern="27[0-9]{9}"
              maxLength={11}
            />
            <p className="text-xs text-muted-foreground">Enter 11-digit number starting with 27</p>
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Button type="submit" size="lg" disabled={loading} className="w-full">
            {loading ? (
              <IconLoader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <IconSearch className="mr-2 h-4 w-4" />
            )}
            {loading ? 'Searching...' : 'Search Wallet'}
          </Button>
        </form>
      </MainCard>

      {wallets.length > 0 && (
        <MainCard>
          <h3 className="text-lg font-semibold p-2 mb-2">
            Found {wallets.length} wallet{wallets.length !== 1 ? 's' : ''}
          </h3>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="font-bold">MSISDN</TableHead>
                <TableHead className="font-bold">Balance</TableHead>
                <TableHead className="font-bold">Status</TableHead>
                <TableHead className="font-bold">Last Update</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {wallets.map((wallet, index) => (
                <TableRow key={index} className="hover:bg-muted/50">
                  <TableCell>{wallet.msisdn}</TableCell>
                  <TableCell>
                    {wallet.balance !== undefined
                      ? `R ${parseFloat(wallet.balance).toFixed(2)}`
                      : 'N/A'}
                  </TableCell>
                  <TableCell>{wallet.status || 'Active'}</TableCell>
                  <TableCell>
                    {wallet.updated_at
                      ? new Date(wallet.updated_at).toLocaleString()
                      : 'N/A'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </MainCard>
      )}

      {!loading && wallets.length === 0 && msisdn && (
        <Alert className="mt-4">
          <AlertDescription>
            No wallet found for this MSISDN. Try searching with a different number.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
